/**
 * SPA Route Change Detector
 *
 * Detects route changes in single-page applications by intercepting:
 * - history.pushState / history.replaceState
 * - popstate events (browser back/forward)
 * - hashchange events (hash-based routing)
 *
 * When a route change is detected, triggers a callback so the fixer
 * client can re-fetch patches for the new URL.
 *
 * SAFETY: Does not break existing pushState/replaceState behavior.
 * Original functions are called first, then we fire our callback.
 */

export type RouteChangeCallback = (newUrl: string, oldUrl: string) => void;

export class RouteDetector {
  private callback: RouteChangeCallback;
  private lastUrl: string;
  private listening = false;

  /** Stored originals for cleanup */
  private origPushState: typeof history.pushState | null = null;
  private origReplaceState: typeof history.replaceState | null = null;
  private boundOnPopState: (() => void) | null = null;
  private boundOnHashChange: (() => void) | null = null;

  /** Debounce timer to avoid firing multiple times for the same navigation */
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private static DEBOUNCE_MS = 50;

  constructor(callback: RouteChangeCallback) {
    this.callback = callback;
    this.lastUrl = window.location.href;
  }

  start(): void {
    if (this.listening) return;
    this.listening = true;

    // Intercept pushState
    this.origPushState = history.pushState;
    const self = this;
    history.pushState = function (...args: Parameters<typeof history.pushState>) {
      self.origPushState!.apply(this, args);
      self.checkUrlChange();
    };

    // Intercept replaceState
    this.origReplaceState = history.replaceState;
    history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
      self.origReplaceState!.apply(this, args);
      self.checkUrlChange();
    };

    // Listen for popstate (back/forward buttons)
    this.boundOnPopState = () => this.checkUrlChange();
    window.addEventListener('popstate', this.boundOnPopState);

    // Listen for hashchange (hash-based routing)
    this.boundOnHashChange = () => this.checkUrlChange();
    window.addEventListener('hashchange', this.boundOnHashChange);
  }

  stop(): void {
    if (!this.listening) return;
    this.listening = false;

    // Restore original history methods
    if (this.origPushState) {
      history.pushState = this.origPushState;
      this.origPushState = null;
    }
    if (this.origReplaceState) {
      history.replaceState = this.origReplaceState;
      this.origReplaceState = null;
    }

    // Remove event listeners
    if (this.boundOnPopState) {
      window.removeEventListener('popstate', this.boundOnPopState);
      this.boundOnPopState = null;
    }
    if (this.boundOnHashChange) {
      window.removeEventListener('hashchange', this.boundOnHashChange);
      this.boundOnHashChange = null;
    }

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /** Get current tracked URL */
  getCurrentUrl(): string {
    return this.lastUrl;
  }

  private checkUrlChange(): void {
    // Debounce to avoid firing multiple times for the same navigation
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      const currentUrl = window.location.href;
      if (currentUrl !== this.lastUrl) {
        const oldUrl = this.lastUrl;
        this.lastUrl = currentUrl;
        try {
          this.callback(currentUrl, oldUrl);
        } catch {
          // Never let callback errors propagate
        }
      }
    }, RouteDetector.DEBOUNCE_MS);
  }
}
